/**
 * Whether the API origin baked into this bundle can possibly work here.
 *
 * `VITE_API_BASE_URL` is substituted by Vite at build time, which means the
 * bundle is specific to the environment it was built for. A build that was
 * handed the wrong value — or no value at all — produces a site that renders
 * its shell, its navigation and its empty states perfectly and cannot reach the
 * API at all. Every screen then blames the reader's data: "je hebt nog geen
 * trainingen" is what a CORS-blocked request looks like from inside the app.
 *
 * The build refuses to run without the variable (see `vite.config.ts`), so the
 * first two problems below should be unreachable. They are still checked here
 * because "should be unreachable" is not a guarantee about a `dist/` somebody
 * copied from somewhere else, and because the cost is one string comparison at
 * boot against a screen that is silently useless.
 *
 * The check is deliberately about the *shape* of the configuration rather than
 * about reachability. Asking the API whether it is there would need a request,
 * a timeout and a decision about what a slow network means; these four
 * conditions are decidable before the first byte goes out, and each one is
 * certainly wrong rather than probably wrong.
 */

/** The one hostname a development build may fall back to. */
export const DEV_API_BASE_URL = 'http://localhost:8090/api';

/**
 * Hosts that only ever mean "this machine".
 *
 * `0.0.0.0` is in here because a dev server bound to all interfaces is often
 * copied out of a terminal that printed it that way.
 */
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0']);

/** Whether a hostname can only resolve on the machine running the browser. */
export function isLoopbackHost(hostname: string): boolean {
    const host = hostname.toLowerCase();

    // The whole `.localhost` TLD is reserved for loopback (RFC 6761), and
    // `thuiscoach.localhost` is a plausible thing to have used locally.
    return LOOPBACK_HOSTS.has(host) || host === '' || host.endsWith('.localhost');
}

/** What is wrong with the configured API origin, if anything. */
export type ApiOriginProblem = 'missing' | 'unparseable' | 'loopback' | 'insecure';

/** The part of `window.location` this check needs. */
export interface PageOrigin {
    hostname: string;
    protocol: string;
}

/**
 * Names the problem with `apiBaseUrl` as seen from the page serving the bundle,
 * or `null` when the configuration is at least self-consistent.
 */
export function apiOriginProblem(apiBaseUrl: string, page: PageOrigin): ApiOriginProblem | null {
    if (apiBaseUrl.trim() === '') {
        return 'missing';
    }

    let api: URL;

    try {
        api = new URL(apiBaseUrl);
    } catch {
        return 'unparseable';
    }

    if (api.protocol !== 'http:' && api.protocol !== 'https:') {
        return 'unparseable';
    }

    /*
     * A loopback API is correct on a developer's machine and never correct
     * anywhere else, so the page's own host is what decides. This is the case
     * the default in `client.ts` used to produce on a deployed site: the built
     * bundle asked the *visitor's* computer for the API.
     */
    if (isLoopbackHost(api.hostname) && !isLoopbackHost(page.hostname)) {
        return 'loopback';
    }

    /*
     * Mixed content. The browser blocks an `http:` request from an `https:`
     * page outright, so this fails exactly like a dead API while the URL in the
     * bundle looks entirely reasonable to whoever typed it. Plesk issues a
     * certificate and redirects to https by default, which makes forgetting the
     * `s` in the variable the most likely single-character mistake in the whole
     * deployment.
     */
    if (page.protocol === 'https:' && api.protocol === 'http:') {
        return 'insecure';
    }

    return null;
}
